import { renderToString } from 'react-dom/server'
import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { getServerState, InstantSearchServerState } from 'react-instantsearch'
import { useContentfulLiveUpdates } from '@contentful/live-preview/react'

import { NotFoundPgPage } from 'pages/[[...slug]]'
import { Layout } from '@components/layouts'
import { getHeaderAndFooter, getPageBySlug } from '@services/contentful'
import { Default404Page } from '@utils/default-404'
import { ContentfulPgPage, ContentType, TemplateContentType } from 'types'

import { TmPageResults } from '@templates/tm-page-results/tm-page-results'

const TmGeneric = dynamic(() => import('@templates/tm-generic').then((mod) => mod.TmGeneric))

export type TmPageResultsProps = {
  CONTENT_TYPE: TemplateContentType
}

export type ServerState = {
  serverState?: InstantSearchServerState
  url?: string
}

export const getServerSideProps: GetServerSideProps<
  (ContentfulPgPage<TmPageResultsProps> & ServerState) | NotFoundPgPage
> = async ({ req, resolvedUrl, preview }) => {
  const path = resolvedUrl.replace(/\?.*/, '').replace('/page-results', 'page-results')

  const isPreview = preview ?? false
  const page = await getPageBySlug<TmPageResultsProps>(path, isPreview)

  if (!page) {
    return { notFound: true }
  }

  const { footerContentful, headerContentful } = await getHeaderAndFooter(page, isPreview)

  if (page.template?.CONTENT_TYPE === 'tmPageResults') {
    const protocol = req.headers.referer?.split('://')[0] || 'https'
    const url = `${protocol}://${req.headers.host}${resolvedUrl}`
    const serverState = await getServerState(<TmPageResults url={url} />, {
      renderToString,
    })

    return {
      props: {
        ...page,
        footer: footerContentful,
        header: headerContentful,
        isPreview,
        serverState,
        url,
        notFound: false,
      },
    }
  }

  return {
    props: {
      ...page,
      footer: footerContentful,
      header: headerContentful,
      isPreview,
      notFound: false,
    },
  }
}

export default function PgPageResultsPage(
  props: (ContentfulPgPage<TmPageResultsProps> & ServerState) | NotFoundPgPage,
) {
  const update = useContentfulLiveUpdates(props)

  if ((update as NotFoundPgPage).notFound) {
    return <Default404Page />
  }

  const { seo, isPreview, header, announcement, footer, slug, template, blocks } =
    update as ContentfulPgPage<TmPageResultsProps>
  const { url, serverState } = update as ServerState

  return (
    <Layout
      slug={slug}
      isPreview={isPreview}
      seo={seo}
      header={{ ...header, announcement: announcement || header.announcement }}
      footer={footer}
    >
      {template?.CONTENT_TYPE === ContentType.TM_PAGE_RESULTS ? (
        <TmPageResults serverState={serverState} url={url} />
      ) : (
        <TmGeneric blocks={blocks} />
      )}
    </Layout>
  )
}
